import _ from "lodash";
import { graphql } from "lib/apolloClient";
import {
  getMypPlannerElementSetsQuery,
  getMypCriterionQuery,
  getToddleLibraryGroupFeedQueryV2,
  getLibraryCommonFeedQueryV2,
  getAssessmentQuestionsForLibraryQueryV2,
} from "./ToddleLibraryQuery";
import {
  getMypPlannerElementSetsFromCache,
  getMypCriterionFromCache,
} from "./ToddleLibraryGraphqlHelpers";
import { getPlannerElementSetsOfCurriculumQuery as getMYPObjectivesQuery } from "modules/CommonQuery";
import { getPlannerElementSetsOfCurriculumFromCache as getMYPObjectivesFromCache } from "modules/CommonGraphqlHelpers";
import { generateQuestionsHierarchy } from "Worksheet/modules/WorksheetUtils";
import { getIsToddleLibraryWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  getLibraryGroupFeedListV2,
  getPaginationInfo,
  getToddleLibraryWorksheetDetails,
  getEntityList,
} from "./ToddleLibraryUtils";
import { prepareLoadMoreFeed } from "Utils";
import { WORKSHEET_ASSESSMENT } from "./Constants";

export const getMypPlannerElementSetsQueryEnhancer = ({
  name,
  variablesKey,
  dataPropName,
  skip = () => false,
}) =>
  graphql(getMypPlannerElementSetsQuery, {
    name,
    skip,
    options: props => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...props[variablesKey],
        portalType: "COMMUNITY",
      },
    }),
    props: ({ [name]: queryResult, ownProps: { isData, isLoading } }) => {
      const { variables, networkStatus } = queryResult;
      const cacheNodes = getMypPlannerElementSetsFromCache(variables);
      const nodes = _.get(cacheNodes, "plannerElementSets.[0].nodes", []);
      return {
        [dataPropName]: nodes,
        isData: !_.isEmpty(nodes) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });

export const getMypCriterionQueryEnhancer = ({
  name,
  variablesKey,
  dataPropName,
  skip = () => false,
}) =>
  graphql(getMypCriterionQuery, {
    name,
    skip,
    options: props => ({
      fetchPolicy: "cache-and-network",
      variables: {
        curriculumProgramId: props[variablesKey],
        portalType: "COMMUNITY",
      },
    }),
    props: ({ [name]: queryResult, ownProps: { isData, isLoading } }) => {
      const { variables, networkStatus } = queryResult;
      const cacheData = getMypCriterionFromCache(variables);
      const nodes = _.get(cacheData, "plannerElementSets.[0].nodes", []);
      return {
        [dataPropName]: nodes,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });

// Enhancer for MYP Objectives (topics/subtopics)
export const getMYPObjectivesQueryEnhancer = ({
  name,
  variablesKey,
  dataPropName,
  skip = () => false,
  pickFields = ["id", "label"],
}) =>
  graphql(getMYPObjectivesQuery, {
    name,
    skip,
    options: props => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...props[variablesKey],
        portalType: "COMMUNITY",
      },
    }),
    props: ({ [name]: queryResult, ownProps: { isData, isLoading } }) => {
      const { variables, networkStatus } = queryResult;
      const nodes = getMYPObjectivesFromCache(variables);
      const mappedNodes = _.map(
        _.get(nodes, "plannerElementSets.[0].nodes", []),
        node => _.pick(node, pickFields)
      );
      return {
        [dataPropName]: mappedNodes,
        isData: !_.isEmpty(nodes) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });

export const getLibraryCommonFeedV2Enhancer = ({
  skip = () => false,
  getEntityType = () => WORKSHEET_ASSESSMENT,
} = {}) =>
  graphql(getLibraryCommonFeedQueryV2, {
    name: "getLibraryCommonFeedV2",
    skip: ownProps =>
      !getIsToddleLibraryWorksheetQueriesV2Enabled() || skip(ownProps),
    options: ({ queryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: queryVariables,
      context: { useHttpLink: true },
    }),
    props({ getLibraryCommonFeedV2, ownProps: { queryVariables, isLoading } }) {
      const { networkStatus } = getLibraryCommonFeedV2;
      const entityObject = _.get(
        getLibraryCommonFeedV2,
        "node.libraryEntities",
        {}
      );
      const rawCategorisedCount = _.omit(
        _.get(entityObject, "categorisedCount"),
        "__typename"
      );
      const categorisedCount = {
        ...rawCategorisedCount,
        question:
          rawCategorisedCount.questionV2 ?? rawCategorisedCount.question,
      };
      const { totalCount, hasNextPage, endCursor } = getPaginationInfo(
        entityObject
      );
      const entityEdges = _.get(entityObject, "edges", []);
      const entityType = getEntityType(queryVariables);
      const feedList = getEntityList(entityEdges, entityType);
      return {
        getLibraryCommonFeed: getLibraryCommonFeedV2,
        feedList,
        totalCount,
        categorisedCount,
        isData: !_.isEmpty(feedList),
        networkStatus,
        hasNextPage,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        loadMoreFeed: prepareLoadMoreFeed({
          query: getLibraryCommonFeedQueryV2,
          variables: { ...queryVariables, after: endCursor },
          queryData: getLibraryCommonFeedV2,
          customPathString: "node.libraryEntities",
        }),
      };
    },
  });

export const getAssessmentQuestionsForLibraryV2Enhancer = () =>
  graphql(getAssessmentQuestionsForLibraryQueryV2, {
    name: "getAssessmentQuestionsForLibraryV2",
    skip: ({ parentId, parentType }) =>
      _.isNil(parentId) ||
      _.isNil(parentType) ||
      !_.isEqual(parentType, "ASSESSMENT") ||
      !getIsToddleLibraryWorksheetQueriesV2Enabled(),
    options: ({ assessmentQuestionsQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: assessmentQuestionsQueryVariables,
    }),
    props({
      getAssessmentQuestionsForLibraryV2,
      ownProps: {
        isData,
        isLoading,
        fetchToddleLibraryWorksheetDetails = false,
      },
    }) {
      const { networkStatus } = getAssessmentQuestionsForLibraryV2;
      const cacheData = _.get(getAssessmentQuestionsForLibraryV2, "node", {});

      const assessmentTitle = _.get(cacheData, "title.value", "");
      const allFields = _.get(cacheData, "allFields", []);
      const descriptionField = _.find(allFields, { uid: "description" });
      const worksheetInstructions = _.get(descriptionField, "value", "");

      const toddleLibraryWorksheetDetails = fetchToddleLibraryWorksheetDetails
        ? getToddleLibraryWorksheetDetails(cacheData)
        : {};

      const hierarchyResult = generateQuestionsHierarchy(
        _.get(cacheData, "questions.edges", [])
      );
      const data = _.isArray(hierarchyResult)
        ? []
        : _.get(hierarchyResult, "data", []);
      const parentItemId = _.get(hierarchyResult, "parentItemId", null);

      return {
        data,
        parentItemId,
        assessmentTitle,
        worksheetInstructions,
        toddleLibraryWorksheetDetails,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        getAssessmentQuestions: getAssessmentQuestionsForLibraryV2,
      };
    },
  });

export const getToddleLibraryGroupFeedV2Enhancer = ({
  skip = () => false,
} = {}) =>
  graphql(getToddleLibraryGroupFeedQueryV2, {
    name: "getToddleLibraryGroupFeedV2",
    skip: ownProps =>
      !getIsToddleLibraryWorksheetQueriesV2Enabled() || skip(ownProps),
    options: ({ queryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: queryVariables,
      context: {
        useHttpLink: true,
      },
    }),
    props({ getToddleLibraryGroupFeedV2, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getToddleLibraryGroupFeedV2;
      const data = _.get(getToddleLibraryGroupFeedV2, "node", {});
      const libraryLanes = _.get(data, "libraryLanes", []);
      return {
        groupFeedList: getLibraryGroupFeedListV2(libraryLanes),
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2, 4], networkStatus) || isLoading,
      };
    },
  });
